import { useState } from "react";
import { createRoute, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { rootRoute } from "./root.tsx";
import { SubPage } from "../components/layout.tsx";
import { Empty, ErrorBlock, Spinner } from "../components/ui.tsx";
import { useIdentities, useMe } from "../api/queries.ts";
import { orgFetch } from "../api/org-client.ts";
import { useIsDesktop } from "../lib/viewport.ts";
import { DesktopTeam } from "./desktop/team.tsx";

/**
 * 我的团队与客户。
 *
 * 🔴 这两样是分销商的核心资产，但之前前端**一个都没接**：
 *    他不知道自己发展了谁、有多少客户、谁是什么时候来的。
 *
 * ⚠️ 客户的手机号在**服务端**就打过码了 —— 前端拿到的本来就是打码的，
 *    不是这里遮一下（前端拿到完整号码就已经泄露）。
 */

export interface TeamMember {
  id: number;
  name: string;
  type: string;
  status: string;
  levelKey: string;
  createdAt: string;
}

export interface Customer {
  userId: number;
  nickname: string;
  /** ⚠️ 服务端已打码。 */
  phone: string;
  boundAt: string;
  expiresAt: string | null;
  source: string;
}

export const TYPE_LABEL: Record<string, string> = {
  dealer: "经销商",
  distributor: "分销商",
  supplier: "供应商",
  platform: "平台",
};

export const SOURCE_LABEL: Record<string, string> = {
  link: "专属链接",
  manual: "人工指定",
  inherit: "上级清退转入",
};

const orgGet = <T,>(path: string, orgId: number) => orgFetch<T>(path, orgId);

function Team() {
  const search = useSearch({ from: "/team" }) as { orgId?: number };
  const { data: me, isPending: mePending } = useMe();
  const { data: identities } = useIdentities(Boolean(me));

  /** 只有经销/分销商有团队和客户；供应商没有这个概念。 */
  const orgs = (identities?.orgs ?? []).filter(
    (o) => o.status === "active" && (o.type === "dealer" || o.type === "distributor"),
  );
  const [picked, setPicked] = useState<number | null>(search.orgId ?? null);
  const orgId = picked ?? orgs[0]?.id ?? null;
  const [tab, setTab] = useState<"team" | "customers">("team");

  const team = useQuery({
    queryKey: ["team", orgId],
    queryFn: () => orgGet<TeamMember[]>("/team", orgId!),
    enabled: Boolean(orgId) && tab === "team",
  });
  const customers = useQuery({
    queryKey: ["customers", orgId],
    queryFn: () => orgGet<{ items: Customer[] }>("/customers?limit=100", orgId!),
    enabled: Boolean(orgId) && tab === "customers",
  });

  if (mePending) {
    return (
      <SubPage title="团队与客户">
        <Spinner />
      </SubPage>
    );
  }
  if (!me || orgs.length === 0) {
    return (
      <SubPage title="团队与客户">
        <Empty title="你还不是经销商或分销商" hint="这是商家功能，联系上级商家或平台开通" />
      </SubPage>
    );
  }

  return (
    <SubPage title="团队与客户">
      {orgs.length > 1 && (
        <div className="flex gap-2 overflow-x-auto bg-white px-4 py-2.5">
          {orgs.map((o) => (
            <button
              key={o.id}
              onClick={() => setPicked(o.id)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-sm ${
                o.id === orgId ? "bg-brand-600 text-white" : "bg-neutral-100 text-ink-500"
              }`}
            >
              {o.name}
            </button>
          ))}
        </div>
      )}

      <div className="flex gap-2 bg-white px-4 py-2.5">
        {(
          [
            ["team", "我发展的下级"],
            ["customers", "我的客户"],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`rounded-full px-3 py-1.5 text-sm ${
              tab === k ? "bg-brand-600 text-white" : "bg-neutral-100 text-ink-500"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "team" && (
        <>
          {/* ⚠️ 说清楚下级也能再往下发展，否则他不知道自己团队会长几层 */}
          <p className="px-4 py-2 text-xs text-ink-500">
            你发展的下级自己也能再发展下级。<strong>只有最近三级参与分佣</strong>，
            再往下的成交不算你的。
          </p>
          {team.isPending ? (
            <Spinner />
          ) : team.error ? (
            <ErrorBlock error={team.error} onRetry={() => team.refetch()} />
          ) : (team.data?.length ?? 0) === 0 ? (
            <Empty title="还没有下级" hint="把专属链接发给想开店的人，他注册后可以申请入驻" />
          ) : (
            <div className="mt-1 bg-white">
              {team.data!.map((m) => (
                <div
                  key={m.id}
                  className="flex items-center gap-3 border-b border-neutral-100 px-4 py-3 last:border-0"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm">{m.name}</p>
                    <p className="mt-0.5 text-xs text-ink-500">
                      {TYPE_LABEL[m.type] ?? m.type}
                      {m.levelKey && ` · ${m.levelKey}`}
                      {` · ${new Date(m.createdAt).toLocaleDateString("zh-CN")} 加入`}
                    </p>
                  </div>
                  {m.status !== "active" && (
                    <span className="shrink-0 rounded bg-neutral-100 px-1.5 py-0.5 text-[11px] text-ink-500">
                      {m.status === "closed" ? "已清退" : m.status}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === "customers" && (
        <>
          <p className="px-4 py-2 text-xs text-ink-500">
            从你的专属链接注册的用户会永久挂在你名下，他们之后下的每一单都算你的。
          </p>
          {customers.isPending ? (
            <Spinner />
          ) : customers.error ? (
            <ErrorBlock error={customers.error} onRetry={() => customers.refetch()} />
          ) : (customers.data?.items.length ?? 0) === 0 ? (
            <Empty title="还没有客户" hint="把专属链接发出去，注册的人就归你了" />
          ) : (
            <div className="mt-1 bg-white">
              {customers.data!.items.map((c) => (
                <div
                  key={c.userId}
                  className="flex items-center gap-3 border-b border-neutral-100 px-4 py-3 last:border-0"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm">{c.nickname || "未设昵称"}</p>
                    <p className="mt-0.5 text-xs text-ink-500">
                      {c.phone} · {SOURCE_LABEL[c.source] ?? c.source} ·{" "}
                      {new Date(c.boundAt).toLocaleDateString("zh-CN")}
                    </p>
                  </div>
                  {/* 归属可以设过期时间（默认永久），到期后这人就不算他的了 */}
                  {c.expiresAt && (
                    <span className="shrink-0 text-[11px] text-amber-600">
                      {new Date(c.expiresAt).toLocaleDateString("zh-CN")} 到期
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </SubPage>
  );
}

/** 手机版 / 网页版是两套页面，按视口挑一套（URL 与数据 hooks 两边共用）。 */
function TeamRoute() {
  return useIsDesktop() ? <DesktopTeam /> : <Team />;
}

export const teamRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/team",
  component: TeamRoute,
  validateSearch: (s: Record<string, unknown>): { orgId?: number } => ({
    orgId: s.orgId ? Number(s.orgId) : undefined,
  }),
});
